import React, { useCallback, useContext, useMemo } from 'react';
import clsx from 'clsx';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { FormattedDate, formatCurrency, useIntl } from '@futrli/localisation';

import { PredictManagementContext } from '../../../predict-management/context/predict-management-context';
import { Data } from '../../../utils/table-filters/filter-table-types';
import * as styles from '../virtualized-list.scss';
import { CUSTOMER_SUPPLIER_KIND } from '../../../forecast/detail/performance/customers-and-suppliers/const';

interface Props {
  rowData?: Data;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onRowClick?: (id: string, type?: string) => void;
  activeRowId?: string;
}

export const CustomersAndSuppliersTableRow = ({ rowData, onRowClick, activeRowId }: Props) => {
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();

  const rowTypeLabel = useMemo(
    () => ({
      [CUSTOMER_SUPPLIER_KIND.CUSTOMER]: intl.formatMessage({ id: 'predict.customer', defaultMessage: 'Customer' }),
      [CUSTOMER_SUPPLIER_KIND.SUPPLIER]: intl.formatMessage({ id: 'predict.supplier', defaultMessage: 'Supplier' })
    }),
    [intl]
  );

  const handleRowClick = useCallback(() => {
    if (onRowClick && rowData) {
      onRowClick(rowData.id, rowData.type);
    }
  }, [onRowClick, rowData]);

  if (!rowData) {
    return null;
  }

  return (
    <div
      role="row"
      key={rowData.id}
      className={clsx(styles.virtualizedListRow, {
        [styles.activeCustomerRow]: activeRowId === rowData.id && rowData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER,
        [styles.activeSupplierRow]: activeRowId === rowData.id && rowData.type === CUSTOMER_SUPPLIER_KIND.SUPPLIER
      })}
      onClick={handleRowClick}
    >
      <div
        className={rowData.type === CUSTOMER_SUPPLIER_KIND.CUSTOMER ? styles.invoicesBorderDiv : styles.billsBorderDiv}
        role="cell"
      />
      <div className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont)} role="cell">
        {rowTypeLabel[(rowData.type as unknown) as CUSTOMER_SUPPLIER_KIND]}
      </div>
      <Tooltip message={<div>{rowData.name}</div>} position="bottom">
        <div
          className={clsx(styles.virtualizedListCell, styles.flex2, styles.tableBodyFont)}
          style={{ minWidth: 0 }}
          role="cell"
        >
          <span className={styles.textOverflow}>{rowData.name}</span>
        </div>
      </Tooltip>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {formatCurrency(Number(rowData.total_amount_due), intl, currency || '', 0, 0)}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {formatCurrency(Number(rowData.total_amount_overdue), intl, currency || '', 0, 0)}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {formatCurrency(Number(rowData.total_amount_invoiced), intl, currency || '', 0, 0)}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {rowData.unpaid_invoices}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {formatCurrency(Number(rowData.average_sale), intl, currency || '', 0, 0)}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {rowData.average_days_to_pay}
      </div>
      <div
        className={clsx(styles.virtualizedListCell, styles.flex1, styles.tableBodyFont, styles.numberValue)}
        role="cell"
      >
        {rowData.dependence}
        {'%'}
      </div>
      <div className={clsx(styles.virtualizedListCell, styles.flex1)} role="cell">
        <FormattedDate value={rowData.last_activity} month={'short'} year="2-digit" day="2-digit" />
      </div>
    </div>
  );
};
